import type {
  Badge,
  Cohort,
  Competency,
  Curriculum,
  Guild,
  MapConnection,
  Quest,
  Region,
  StudentProgress,
  UserProfile,
} from "../types/domain";

export const GUILDS: Guild[] = [
  {
    id: "explorer",
    name: "Explorer Guild",
    shortName: "Explorer",
    description: "Discover every path and connect ideas across the curriculum.",
    color: "#38bdf8",
  },
  {
    id: "creator",
    name: "Creator Guild",
    shortName: "Creator",
    description: "Build the mathematical systems that make games work.",
    color: "#fb923c",
  },
  {
    id: "designer",
    name: "Designer Guild",
    shortName: "Designer",
    description: "Shape feel, balance, polish, and playful experiences.",
    color: "#34d399",
  },
  {
    id: "ai",
    name: "AI Adventurer Guild",
    shortName: "AI",
    description: "Use mathematical reasoning to create intelligent behavior.",
    color: "#c084fc",
  },
  {
    id: "challenger",
    name: "Challenger Guild",
    shortName: "Challenger",
    description: "Seek advanced routes, bosses, and high-skill challenges.",
    color: "#fb7185",
  },
];

export const REGIONS: Region[] = [
  {
    id: "quantity-quay",
    order: 1,
    name: "Quantity Quay",
    topic: "Numbers, Scalars & Representation",
    description:
      "Begin at a bustling harbor where every cargo, scale, and switch reveals how computers represent quantity.",
    icon: "Anchor",
    color: "#14b8a6",
    x: 10,
    y: 72,
    xpAvailable: 1050,
    prerequisiteRegionIds: [],
    subAreas: [
      {
        id: "magnitude-marina",
        name: "Magnitude Marina",
        summary: "Numbers as magnitudes and scalar effects.",
        order: 1,
      },
      {
        id: "integer-harbour",
        name: "Integer Harbour",
        summary: "Binary integer representation.",
        order: 2,
      },
      {
        id: "floating-docks",
        name: "Floating Docks",
        summary: "Binary real-number representation.",
        order: 3,
      },
    ],
  },
  {
    id: "coordinate-crossroads",
    order: 2,
    name: "Coordinate Crossroads",
    topic: "Coordinate Systems",
    description:
      "Navigate local and world spaces across tactical grids, moving platforms, and hidden paths.",
    icon: "MapPinned",
    color: "#0ea5e9",
    x: 22,
    y: 53,
    xpAvailable: 800,
    prerequisiteRegionIds: ["quantity-quay"],
    subAreas: [
      {
        id: "cartesian-corner",
        name: "Cartesian Corner",
        summary: "Plot and navigate precise positions.",
        order: 1,
      },
      {
        id: "grid-junction",
        name: "Grid Junction",
        summary: "Translate local coordinates into world coordinates.",
        order: 2,
      },
    ],
  },
  {
    id: "vector-valley",
    order: 3,
    name: "Vector Valley",
    topic: "Vectors & Movement",
    description:
      "Guide gliders and drones with direction, displacement, magnitude, and vector composition.",
    icon: "Navigation",
    color: "#10b981",
    x: 34,
    y: 73,
    xpAvailable: 1250,
    prerequisiteRegionIds: ["coordinate-crossroads"],
    subAreas: [
      {
        id: "arrowhead-acres",
        name: "Arrowhead Acres",
        summary: "Add vectors tip-to-tail.",
        order: 1,
      },
      {
        id: "distance-dunes",
        name: "Distance Dunes",
        summary: "Calculate distance between points.",
        order: 2,
      },
      {
        id: "displacement-delta",
        name: "Displacement Delta",
        summary: "Find and compensate displacement vectors.",
        order: 3,
      },
    ],
  },
  {
    id: "geometry-gulch",
    order: 4,
    name: "Geometry Gulch",
    topic: "Shapes in Space",
    description:
      "Build bounding forms and low-poly structures in a rugged canyon of circles, boxes, and meshes.",
    icon: "Shapes",
    color: "#6366f1",
    x: 45,
    y: 38,
    xpAvailable: 1250,
    prerequisiteRegionIds: ["vector-valley"],
    subAreas: [
      {
        id: "radius-ridge",
        name: "Radius Ridge",
        summary: "Circles, spheres, centers, and radii.",
        order: 1,
      },
      {
        id: "block-basin",
        name: "Block Basin",
        summary: "AABBs, OBBs, squares, and cubes.",
        order: 2,
      },
      {
        id: "polygon-pass",
        name: "Polygon Pass",
        summary: "Triangles, meshes, and winding order.",
        order: 3,
      },
    ],
  },
  {
    id: "trigonometry-tower",
    order: 5,
    name: "Trigonometry Tower",
    topic: "Angles & Rotations",
    description:
      "Climb a luminous tower where launch arcs, unit circles, and angular motion power ancient defenses.",
    icon: "Crosshair",
    color: "#a855f7",
    x: 56,
    y: 61,
    xpAvailable: 1000,
    prerequisiteRegionIds: ["vector-valley"],
    subAreas: [
      {
        id: "archers-ascent",
        name: "Archer's Ascent",
        summary: "Aim with sine, cosine, and launch angles.",
        order: 1,
      },
      {
        id: "rotation-roost-trig",
        name: "Rotation Roost",
        summary: "Degrees, radians, and the unit circle.",
        order: 2,
      },
    ],
  },
  {
    id: "matrix-manor",
    order: 6,
    name: "Matrix Manor",
    topic: "Matrix Operations & Transformations",
    description:
      "Unlock a grand estate of computations, transforms, inverses, hierarchies, and game-balance systems.",
    icon: "Grid3X3",
    color: "#f43f5e",
    x: 66,
    y: 78,
    xpAvailable: 1600,
    prerequisiteRegionIds: ["geometry-gulch", "trigonometry-tower"],
    subAreas: [
      {
        id: "computation-court",
        name: "Computation Court",
        summary: "Matrix arithmetic and multiplication.",
        order: 1,
      },
      {
        id: "hall-transformations",
        name: "The Hall of Transformations",
        summary: "Translate, rotate, scale, and order transforms.",
        order: 2,
      },
      {
        id: "chamber-inverses",
        name: "Chamber of Inverses",
        summary: "Reverse transformations with inverse matrices.",
        order: 3,
      },
      {
        id: "game-balance-bridge",
        name: "Game Balance Bridge",
        summary: "Tune weighted systems and deformation.",
        order: 4,
      },
    ],
  },
  {
    id: "newtonian-nexus",
    order: 7,
    name: "Newtonian Nexus",
    topic: "Game Physics",
    description:
      "Tune motion, forces, and numerical integration in an energetic laboratory archipelago.",
    icon: "Zap",
    color: "#f59e0b",
    x: 76,
    y: 48,
    xpAvailable: 1600,
    prerequisiteRegionIds: ["trigonometry-tower"],
    subAreas: [
      {
        id: "velocity-village",
        name: "Velocity Village",
        summary: "Synchronize constant velocity systems.",
        order: 1,
      },
      {
        id: "acceleration-apex",
        name: "Acceleration Apex",
        summary: "Tune changing velocity over time.",
        order: 2,
      },
      {
        id: "force-foundry",
        name: "Force Foundry",
        summary: "Balance net force and equilibrium.",
        order: 3,
      },
      {
        id: "integration-island",
        name: "Integration Island",
        summary: "Explore stable real-time simulation.",
        order: 4,
      },
    ],
  },
  {
    id: "collision-canyon",
    order: 8,
    name: "Collision Canyon",
    topic: "Collision Detection & Response",
    description:
      "Detect contact, choose efficient hitboxes, and resolve collisions through a perilous canyon.",
    icon: "Boxes",
    color: "#ec4899",
    x: 84,
    y: 25,
    xpAvailable: 1300,
    prerequisiteRegionIds: ["geometry-gulch", "newtonian-nexus"],
    subAreas: [
      {
        id: "hitbox-hollow",
        name: "Hitbox Hollow",
        summary: "Balance collision accuracy and cost.",
        order: 1,
      },
      {
        id: "contact-cliffs",
        name: "Contact Cliffs",
        summary: "Normals, reflection, and response.",
        order: 2,
      },
      {
        id: "sat-summit",
        name: "SAT Summit",
        summary: "Prove separation between convex shapes.",
        order: 3,
      },
    ],
  },
  {
    id: "rotation-roost",
    order: 9,
    name: "Rotation Roost",
    topic: "Rotation Representations",
    description:
      "Master Euler angles, angle-axis rotations, and quaternions high above the game world.",
    icon: "Orbit",
    color: "#8b5cf6",
    x: 68,
    y: 17,
    xpAvailable: 1350,
    prerequisiteRegionIds: ["matrix-manor"],
    subAreas: [
      {
        id: "eulers-end",
        name: "Euler's End",
        summary: "Steer with roll, pitch, yaw, and gimbal lock.",
        order: 1,
      },
      {
        id: "angle-axis-alley",
        name: "Angle-Axis Alley",
        summary: "Rotate around an arbitrary axis.",
        order: 2,
      },
      {
        id: "quaternion-courtyard",
        name: "Quaternion Courtyard",
        summary: "Interpolate orientation smoothly with SLERP.",
        order: 3,
      },
    ],
  },
];

const routes: Array<[string, string, number, number]> = [
  ["quantity-quay", "coordinate-crossroads", 14, 58],
  ["coordinate-crossroads", "vector-valley", 29, 67],
  ["vector-valley", "geometry-gulch", 37, 49],
  ["vector-valley", "trigonometry-tower", 47, 71],
  ["geometry-gulch", "trigonometry-tower", 52, 44],
  ["geometry-gulch", "matrix-manor", 55, 66],
  ["trigonometry-tower", "matrix-manor", 62, 72],
  ["trigonometry-tower", "newtonian-nexus", 67, 55],
  ["newtonian-nexus", "collision-canyon", 82, 38],
  ["matrix-manor", "rotation-roost", 63, 45],
];

export const CONNECTIONS: MapConnection[] = GUILDS.flatMap((guild, guildIndex) =>
  routes.map(([fromRegionId, toRegionId, controlX, controlY], index) => ({
    id: `${guild.id}-${index + 1}`,
    fromRegionId,
    toRegionId,
    guild: guild.id,
    controlX: controlX + (guildIndex - 2) * 0.8,
    controlY: controlY + (guildIndex - 2) * 1.3,
  })),
);

export const COMPETENCIES: Competency[] = REGIONS.flatMap((region) =>
  region.subAreas.map((subArea, index) => ({
    id: `${subArea.id}-competency`,
    name: subArea.name.replace(/(Acres|Dunes|Delta|Corner|Junction|Marina|Harbour|Docks|Ridge|Basin|Pass|Ascent|Roost|Court|Hall|Chamber|Bridge|Village|Apex|Foundry|Island|Hollow|Cliffs|Summit|End|Alley|Courtyard)$/i, "").trim() || subArea.name,
    regionId: region.id,
    description: subArea.summary,
    core: index === 0,
  })),
);

const comingSoonQuest = (
  region: Region,
  subArea: Region["subAreas"][number],
): Quest => ({
  id: `${subArea.id}-quest`,
  regionId: region.id,
  subAreaId: subArea.id,
  title: `${subArea.name} Challenge`,
  storyHook: `A new disturbance has appeared in ${subArea.name}. Its full interactive expedition is being forged.`,
  learningGoal: subArea.summary,
  practice: "Review the region codex and inspect the upcoming challenge briefing.",
  reflectionPrompt: "Where might this idea appear in a game you have played or built?",
  templateId: "coming-soon",
  templateVersion: 1,
  config: {},
  xp: 300,
  gradePoints: 10,
  xpDecay: 0.2,
  gradeDecay: 0.1,
  cooldownMinutes: 0,
  maxAttempts: null,
  competencyEvidence: { [`${subArea.id}-competency`]: 25 },
  boss: false,
  status: "published",
  version: 1,
});

const baseQuests = REGIONS.flatMap((region) =>
  region.subAreas.map((subArea) => comingSoonQuest(region, subArea)),
);

const replaceQuest = (quest: Quest): Quest[] =>
  baseQuests.map((candidate) =>
    candidate.regionId === quest.regionId &&
    candidate.subAreaId === quest.subAreaId
      ? quest
      : candidate,
  );

let quests = replaceQuest({
  ...comingSoonQuest(REGIONS[2], REGIONS[2].subAreas[0]),
  id: "vector-drone-rescue",
  title: "Guide the Stranded Delivery Drone",
  storyHook:
    "A delivery drone has lost its navigation target inside Vector Valley. Strong winds are pushing it away from the extraction ring.",
  learningGoal:
    "Compose propulsion and wind vectors to produce a target resultant.",
  practice:
    "Inspect the wind and target. Predict the opposite wind vector before adjusting the propulsion controls.",
  reflectionPrompt:
    "How did changing one component affect the final resultant vector?",
  templateId: "vector-drone",
  config: {
    difficulty: "practice",
    windXMin: 20,
    windXMax: 60,
    windYMin: -25,
    windYMax: 25,
    targetXMin: 80,
    targetXMax: 150,
    targetYMin: 35,
    targetYMax: 105,
    inputMin: -120,
    inputMax: 160,
    tolerance: 8,
  },
  xp: 350,
  gradePoints: 15,
  competencyEvidence: { "arrowhead-acres-competency": 35 },
});
baseQuests.splice(0, baseQuests.length, ...quests);

quests = replaceQuest({
  ...comingSoonQuest(REGIONS[4], REGIONS[4].subAreas[0]),
  id: "tower-deflector",
  title: "Re-align the Tower Deflector",
  storyHook:
    "Glitch spheres are approaching Trigonometry Tower. Aim the ancient deflector manually before they cross the ward.",
  learningGoal:
    "Resolve launch velocity into horizontal and vertical components.",
  practice:
    "Change the angle and observe how the predicted trajectory bends while power remains constant.",
  reflectionPrompt:
    "Why can two different launch angles sometimes reach a similar horizontal distance?",
  templateId: "trig-cannon",
  config: {
    gravity: 9.8,
    targetXMin: 45,
    targetXMax: 68,
    targetYMin: 12,
    targetYMax: 27,
    angleMin: 15,
    angleMax: 75,
    powerMin: 18,
    powerMax: 35,
    tolerance: 3,
  },
  xp: 400,
  gradePoints: 15,
  competencyEvidence: { "archers-ascent-competency": 35 },
});
baseQuests.splice(0, baseQuests.length, ...quests);

quests = replaceQuest({
  ...comingSoonQuest(REGIONS[6], REGIONS[6].subAreas[1]),
  id: "perfect-platformer-jump",
  title: "Build the Perfect Platformer Jump",
  storyHook:
    "The hero's jump is floaty, heavy, and inconsistent. Tune the motion until it clears the obstacle and lands on the safe platform.",
  learningGoal:
    "Relate launch velocity, gravity, time, and displacement in a game jump.",
  practice:
    "Compare the effect of increasing launch velocity with decreasing gravity.",
  reflectionPrompt:
    "Which setting changed game feel most strongly, and why?",
  templateId: "platformer-jump",
  config: {
    gravityMin: 6,
    gravityMax: 18,
    velocityMin: 8,
    velocityMax: 20,
    obstacleDistance: 10,
    obstacleHeight: 3.2,
    landingMin: 22,
    landingMax: 30,
    horizontalSpeed: 8,
    simulationStep: 0.016,
  },
  xp: 450,
  gradePoints: 20,
  competencyEvidence: { "acceleration-apex-competency": 40 },
});
baseQuests.splice(0, baseQuests.length, ...quests);

export const QUESTS = baseQuests;

export const INITIAL_CURRICULUM: Curriculum = {
  id: "10000000-0000-0000-0000-000000000001",
  courseId: "essential-math-games",
  version: 1,
  status: "published",
  updatedAt: new Date().toISOString(),
  regions: REGIONS,
  connections: CONNECTIONS,
  competencies: COMPETENCIES,
  quests: QUESTS,
};

export const DEMO_STUDENT: UserProfile = {
  id: "student-alex",
  fullName: "Alex Gamedev",
  email: "alex@example.edu",
  role: "student",
  guild: "creator",
  level: 3,
  xp: 920,
  xpNext: 1200,
  guildReputation: {
    explorer: 120,
    creator: 480,
    designer: 90,
    ai: 60,
    challenger: 25,
  },
  lastActiveAt: new Date().toISOString(),
};

export const DEMO_INSTRUCTOR: UserProfile = {
  id: "instructor-hogue",
  fullName: "Professor Hogue",
  email: "faculty@example.edu",
  role: "instructor",
  guild: "explorer",
  level: 12,
  xp: 0,
  xpNext: 1,
  guildReputation: {
    explorer: 1000,
    creator: 1000,
    designer: 1000,
    ai: 1000,
    challenger: 1000,
  },
  lastActiveAt: new Date().toISOString(),
};

const starterBadges: Badge[] = [
  {
    id: "first-step",
    name: "Plaza Pioneer",
    description: "Began the journey through the mathematics of games.",
    icon: "Footprints",
    earnedAt: new Date(Date.now() - 7 * 86_400_000).toISOString(),
  },
  {
    id: "vector-rookie",
    name: "Vector Rookie",
    description: "Successfully composed a resultant movement vector.",
    icon: "Navigation",
    earnedAt: new Date(Date.now() - 2 * 86_400_000).toISOString(),
  },
];

export const DEMO_PROGRESS: StudentProgress = {
  completedQuestIds: [],
  regionCompletion: {
    "quantity-quay": 100,
    "coordinate-crossroads": 78,
    "vector-valley": 34,
    "geometry-gulch": 0,
    "trigonometry-tower": 0,
    "matrix-manor": 0,
    "newtonian-nexus": 0,
    "collision-canyon": 0,
    "rotation-roost": 0,
  },
  competencyEvidence: Object.fromEntries(
    COMPETENCIES.map((competency, index) => [
      competency.id,
      index === 0 ? 80 : index < 3 ? 48 : index < 6 ? 12 : 0,
    ]),
  ),
  attempts: [],
  badges: starterBadges,
};

const demoStudent = (
  id: string,
  fullName: string,
  guild: UserProfile["guild"],
  daysInactive: number,
): UserProfile => ({
  ...DEMO_STUDENT,
  id,
  fullName,
  email: `${id}@example.edu`,
  guild,
  lastActiveAt: new Date(Date.now() - daysInactive * 86_400_000).toISOString(),
});

export const DEMO_STUDENTS: UserProfile[] = [
  DEMO_STUDENT,
  demoStudent("student-marcus", "Marcus Brody", "designer", 1),
  demoStudent("student-elena", "Elena Rostova", "challenger", 0),
  demoStudent("student-dave", "Dave Finch", "ai", 12),
  demoStudent("student-sarah", "Sarah Connor", "creator", 3),
  demoStudent("student-ken", "Ken Masters", "explorer", 18),
];

export const DEMO_STUDENT_PROGRESS: Record<string, StudentProgress> =
  Object.fromEntries(
    DEMO_STUDENTS.map((student, index) => {
      if (index === 0) return [student.id, DEMO_PROGRESS];
      const factor = [0.48, 0.72, 0.9, 0.13, 0.61, 0.05][index];
      const attempts = index === 3
        ? [1, 2, 3].map((attemptNumber) => ({
            id: `failed-${attemptNumber}`,
            questId: "vector-drone-rescue",
            questVersion: 1,
            studentId: student.id,
            seed: 200 + attemptNumber,
            attemptNumber,
            status: "failed" as const,
            startedAt: new Date(
              Date.now() - attemptNumber * 3_600_000,
            ).toISOString(),
            completedAt: new Date(
              Date.now() - attemptNumber * 3_500_000,
            ).toISOString(),
            awardedXp: 0,
            awardedGradePoints: 0,
          }))
        : [];
      return [
        student.id,
        {
          ...DEMO_PROGRESS,
          regionCompletion: Object.fromEntries(
            REGIONS.map((region, regionIndex) => [
              region.id,
              Math.max(0, Math.min(100, Math.round(factor * 140 - regionIndex * 13))),
            ]),
          ),
          competencyEvidence: Object.fromEntries(
            COMPETENCIES.map((competency, competencyIndex) => [
              competency.id,
              Math.max(0, Math.min(100, Math.round(factor * 115 - competencyIndex * 2))),
            ]),
          ),
          attempts,
          badges: index === 0 ? starterBadges : starterBadges.slice(0, 1),
        },
      ];
    }),
  );

export const DEMO_COHORT: Cohort = {
  id: "cohort-fall-2026",
  courseId: "essential-math-games",
  name: "Essential Math for Games - Section 01",
  term: "Fall 2026",
  startsAt: "2026-09-08",
  endsAt: "2026-12-18",
  curriculumVersion: 1,
  studentIds: DEMO_STUDENTS.map((student) => student.id),
  instructorIds: [DEMO_INSTRUCTOR.id],
  riskRules: {
    inactivityDays: 7,
    expectedProgressRatio: 0.6,
    recentFailureCount: 3,
    coreCompetencyOverdueDays: 14,
  },
};
